<template>
  <div class="flex h-full overflow-hidden">
    <!-- NO ITEM ICON -->
    <div v-show="!isLoading && totalAssets === 0" class="self-center h-64 m-auto">
      <div class="h-3 mb-10 text-xs text-center text-dark-25">
        {{ t('assetsList.noAssets') }}
      </div>
      <no-items-image style="width: 106px; height: 150px;" class="m-auto" />
    </div>

    <div
      v-show="isLoading || totalAssets > 0" id="scrollView" ref="scrollViewList"
      class="w-full h-auto overflow-auto pb-13"
    >
      <!-- Skeleton cards when loading -->
      <div v-show="isLoading" class="w-full h-full border-separate" :class="`t-${thumbnailSize}`">
        <div v-for="artIndex in 50" :key="artIndex" class="float-left thumbnail" data-id="skele-1">
          <asset-thumbnail-skeleton :size="thumbnailSize" />
        </div>
      </div>

      <div v-show="!isLoading" id="articlesListTable" class="w-full h-full border-separate" :class="`t-${thumbnailSize}`">
        <!-- COLUMNS (IF USER PIVOT AND SELECT A COLUMN) -->

        <div
          v-for="asset in assets" :key="asset.document.Id"
          class="float-left thumbnail" :data-id="asset.document.Id"
        >
          <asset-thumbnail
            v-model:selected="selectedItems[asset.document.Id]" :asset="asset" :context="duneContext" :context-key="contextKey"
            :selection-mode="selectedAssetIds.length > 0" :size="thumbnailSize" :allow-selection="props.allowSelection"
            @selection-changed="selectionChanged" @click="onAssetClick(asset)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import type { AxiosResponse } from 'axios'
import AssetThumbnailSkeleton from './AssetThumbnailSkeleton.vue'
import AssetThumbnail from './AssetThumbnail.vue'
import type { SearchAssetModel, SearchAssetResponse } from '@/api/dune/model/searchAssetModel'
import { filterAssets, nlpSearchAssets, reversLookupAssets } from '@/api/dune/asset'
import { assetsConstants } from '@/models/constants'
import NoItemsImage from '@/shared/components/svg/NoItemsImage.vue'
import { useUserStore } from '@/store/userData'
import utils from '@/services/utils'

interface IProps {
  thumbnailSize: number
  searchText: string
  filter: string
  imageData?: string | null
  allowSelection?: boolean
  searchMode: 'full-text' | 'filter' | 'nlp' | 'reverse-lookup'
}

const props = withDefaults(defineProps<IProps>(), {
  thumbnailSize: 1,
  searchText: '*',
  filter: '',
  allowSelection: false,
  searchMode: 'full-text',
})

const emit = defineEmits<{
  (e: 'assetClick', asset: SearchAssetModel): void
  (e: 'selectionChanged', assets: SearchAssetModel[]): void
  (e: 'dataChanged', assets: SearchAssetModel[]): void
}>()

const { t } = useI18n()
const userStore = useUserStore()

const scrollViewList = ref<HTMLElement>()
const loading = ref(false)
const assets = ref<SearchAssetModel[]>([])
const totalAssets = ref(0)
const selectedItems = ref({} as { [param: string]: boolean })
const duneContext = computed(() => {
  return userStore.activeCatalog?.DuneContext ?? userStore.sellerDetails?.DuneContext ?? ''
})
const contextKey = computed(() => {
  return userStore.activeCatalog?.ContextKey ?? userStore.sellerDetails?.ContextKey ?? ''
})
const selectedAssetIds = computed(() => {
  return assets.value.filter(asset => selectedItems.value[asset.document.Id]).map(asset => asset.document.Id)
})

// show loading until user store is set
const isUserStoreReady = computed(() => {
  return !!(utils.isDefined(userStore.activeCatalog) || utils.isDefined(userStore.sellerDetails))
})

const isLoading = computed(() => {
  if (!isUserStoreReady.value) { return true }

  return loading.value
})

async function reloadAssets() {
  if (isLoading.value) { return }

  loading.value = true
  assets.value = []
  totalAssets.value = 0
  selectedItems.value = {}

  if (!duneContext.value) {
    loading.value = false
    return
  }

  let p: Promise<AxiosResponse<SearchAssetResponse, any>>
  if (props.searchMode === 'nlp' && props.searchText && props.searchText.trim().length > 0) {
    // Perform NLP-based search
    p = nlpSearchAssets(duneContext.value, contextKey.value, props.searchText, assetsConstants.maxAssets, 0)
  }
  else if (props.searchMode === 'reverse-lookup') {
    // Perform reverse image lookup
    if (!props.imageData) {
      console.error('No image data provided for reverse lookup')
      loading.value = false
      return
    }
    p = reversLookupAssets(duneContext.value, contextKey.value, props.imageData.split(',')[1], assetsConstants.maxAssets, 0)
  }
  else {
    // Perform regular search
    const text = props.searchText && props.searchText.trim().length > 0 ? props.searchText : '*'
    p = filterAssets(duneContext.value, contextKey.value, text, props.filter, assetsConstants.maxAssets, 0)
  }

  p.then((response) => {
    assets.value = response.data.items.filter(asset => asset.score >= 0.8)
    totalAssets.value = response.data.count || assets.value.length
    selectedItems.value = assets.value.reduce((acc, item) => {
      acc[item.document.Id] = false
      return acc
    }, {} as { [param: string]: boolean })
  })
    .catch((error) => {
      console.error('Error fetching assets:', error)
      assets.value = []
      totalAssets.value = 0
    })
    .finally(() => {
      loading.value = false

      emit('dataChanged', assets.value)
      emit('selectionChanged', assets.value.filter(asset => selectedItems.value[asset.document.Id]))
    })
}

function selectionChanged() {
  emit('selectionChanged', assets.value.filter(a => selectedItems.value[a.document.Id]))
}

function deselectAll() {
  Object.entries(selectedItems.value).forEach(itm => selectedItems.value[itm[0]] = false)
  selectionChanged()
}

function selectAll() {
  Object.entries(selectedItems.value).forEach(itm => selectedItems.value[itm[0]] = true)
  selectionChanged()
}

function onAssetClick(asset: SearchAssetModel) {
  emit('assetClick', asset)
}

watch(
  () => [duneContext, contextKey, props.searchText, props.filter, props.searchMode, props.imageData],
  () => {
    reloadAssets()
  },
  { immediate: true },
)

watch(
  () => isUserStoreReady.value,
  (val) => {
    if (val && !isLoading.value) {
      reloadAssets()
    }
  },
  { immediate: true },
)

defineExpose({
  selectAll,
  deselectAll,
  reloadAssets,
})
</script>

<style lang="scss" scoped>
.t-1 {

  .thumbnail {
    margin: 4px;
  }
}

.t-2 {

  .thumbnail {
    margin: 10px;
  }
}

.t-3 {

  .thumbnail {
    margin: 10px;
  }
}

.highlight-article {
  outline: 1px solid rgb(33 150 243);
  transition: outline 0.3s ease-in-out;
}
</style>
